import { useState } from "react";
import { MessageCircle, Check, Banknote, Landmark } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { chaseMessage, markPaid, payMessage, whatsAppUrl, type LinkOverride } from "@/lib/quickActions";
import type { BookingWithClient } from "@/lib/bookingsApi";

const pill = "inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-inter transition-colors min-h-[40px]";

export const WhatsAppButton = ({ booking, chase = false, compact = false, group }: { booking: BookingWithClient; chase?: boolean; compact?: boolean; group?: LinkOverride }) => (
  <a
    href={whatsAppUrl(booking.clients?.phone, chase ? chaseMessage(booking, group) : payMessage(booking, group))}
    target="_blank"
    rel="noopener noreferrer"
    onClick={(e) => e.stopPropagation()}
    className={`${pill} border border-primary/40 text-primary hover:bg-primary/10`}
  >
    <MessageCircle className="w-4 h-4" />
    {compact ? (chase ? "Nudge" : "WhatsApp") : chase ? "Send reminder on WhatsApp" : "Send pay link on WhatsApp"}
  </a>
);

export const MarkPaidButton = ({ booking, onDone }: { booking: BookingWithClient; onDone: () => void }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (booking.payment_status === "paid") return null;
  const go = async (method: "bank_transfer" | "cash") => {
    setBusy(true);
    setErr(null);
    const r = await markPaid(booking, method, user?.id);
    setBusy(false);
    if (r.ok === false) setErr(r.error);
    else onDone();
  };
  if (!open)
    return (
      <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }} className={`${pill} bg-primary text-primary-foreground hover:bg-primary/90`}>
        <Check className="w-4 h-4" /> Mark as paid
      </button>
    );
  return (
    <span className="inline-flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <span className="text-xs text-white/50 font-inter">Paid by</span>
      <button type="button" disabled={busy} onClick={() => go("bank_transfer")} className={`${pill} bg-primary text-primary-foreground disabled:opacity-50`}>
        <Landmark className="w-4 h-4" /> Bank
      </button>
      <button type="button" disabled={busy} onClick={() => go("cash")} className={`${pill} bg-primary text-primary-foreground disabled:opacity-50`}>
        <Banknote className="w-4 h-4" /> Cash
      </button>
      <button type="button" onClick={() => setOpen(false)} className="text-xs text-white/50 underline">Cancel</button>
      {err && <span className="text-xs text-destructive w-full">{err}</span>}
    </span>
  );
};
